import React from 'react';
import { Link } from 'react-router-dom';

export default function BelaegningContentSection() {
  const pavingFeatures = [
    {
      title: 'Korrekt opbygget bærelag',
      desc: 'Grundig udgravning, bundsikringssand og komprimeret stabilgrus forhindrer kørespor, lunker og sætninger.',
      icon: 'fa-duotone fa-layer-group',
      badge: 'Bærelag'
    },
    {
      title: 'Nøjagtig laserafretning & fald',
      desc: 'Præcist fald bort fra sokkel og murværk sikrer, at regnvand ledes sikkert væk til dræn og afløb.',
      icon: 'fa-duotone fa-ruler-triangle',
      badge: 'Afvanding'
    },
    {
      title: 'Mange typer fliser & sten',
      desc: 'Lægning af betonfliser, herregårdssten, brosten, granit og chaussésten i harmoniske mønstre.',
      icon: 'fa-duotone fa-grid-horizontal',
      badge: 'Materialer'
    },
    {
      title: 'Holdbare kanter & støttemure',
      desc: 'Solide kantsikringer og støttemure holder belægningen på plads mod skridning ved tung belastning.',
      icon: 'fa-duotone fa-trowel-bricks',
      badge: 'Stabilitet'
    }
  ];

  const processSteps = [
    {
      step: '01',
      phase: 'Trin 1',
      title: 'Rådgivning & Opmåling',
      text: 'Vi gennemgår arealet sammen, vurderer jordbunden, terrænfaldet og drøfter stenvalg, mønstre og adgangsforhold.',
      icon: 'fa-duotone fa-compass-drafting'
    },
    {
      step: '02',
      phase: 'Trin 2',
      title: 'Udgravning & Bundopbygning',
      text: 'Muldjorden afrømmes til bæredygtig dybde. Bærelaget opbygges med stabilgrus og komprimeres lag for lag med pladevibrator.',
      icon: 'fa-duotone fa-shovel'
    },
    {
      step: '03',
      phase: 'Trin 3',
      title: 'Afretning & Afvanding',
      text: 'Afretningsgrus udlægges og trækkes snorlige af med korrekt fald mod tagnedløb, regnvandsbrønde eller drænsystemer.',
      icon: 'fa-duotone fa-ruler-horizontal'
    },
    {
      step: '04',
      phase: 'Trin 4',
      title: 'Lægning, Fugning & Slutkontrol',
      text: 'Fliserne lægges med fugeafstand, kantsikres og vibreres med fugefint sand for en stærk, låst overflade.',
      icon: 'fa-duotone fa-badge-check'
    }
  ];

  const priceFactors = [
    {
      title: 'Arealets størrelse & form',
      desc: 'Antal kvadratmeter og om der er mange skæringer, kurver, trapper eller vinkler.',
      icon: 'fa-solid fa-ruler-combined'
    },
    {
      title: 'Jordforhold & udgravningsdybde',
      desc: 'Hvor meget muldjord der skal bortskaffes, og dybden af det nødvendige bærelag.',
      icon: 'fa-solid fa-layer-group'
    },
    {
      title: 'Valg af sten & fliser',
      desc: 'Prisforskel på standard betonfliser, modulfliser, herregårdssten eller natursten.',
      icon: 'fa-solid fa-gem'
    },
    {
      title: 'Afvanding & integreret dræn',
      desc: 'Behov for linjedræn, regnvandsbrønde, tagnedløb eller tilslutning til kloaksystem.',
      icon: 'fa-solid fa-pipe-valve'
    },
    {
      title: 'Kantsikring & støttemure',
      desc: 'Støbte kantsikringer eller opbygning af støttemure ved niveauforskelle i haven.',
      icon: 'fa-solid fa-trowel'
    },
    {
      title: 'Adgangsforhold for maskiner',
      desc: 'Pladsforhold for gravemaskiner, minilæssere og lastbiler med råstoffer.',
      icon: 'fa-solid fa-truck-ramp-couch'
    }
  ];

  const serviceTowns = [
    { name: 'Sommersted', isBase: true },
    { name: 'Jels', isBase: true },
    { name: 'Haderslev', isBase: false },
    { name: 'Vojens', isBase: false },
    { name: 'Vejen', isBase: false },
    { name: 'Kolding', isBase: false },
    { name: 'Gram', isBase: false }
  ];

  return (
    <div className="space-y-12">
      {/* Intro Banner */}
      <div className="rounded-xl border border-brand-orange-500/20 bg-gradient-to-br from-brand-navy-900 to-brand-navy-950 p-6 md:p-8 text-white shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-6 -mr-6 w-36 h-36 bg-brand-orange-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-orange-500/20 border border-brand-orange-400/30 text-brand-orange-300 text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-award" /> Kvalitetsarbejde & Holdbare Løsninger
            </div>
            <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-white leading-snug">
              Professionelt belægningsarbejde med et solidt resultat
            </h2>
            <p className="text-brand-navy-200 text-base md:text-lg leading-relaxed">
              En flot belægning fuldender husets udtryk og skaber funktionelle rammer om din hverdag. Hos Entreprenør Jan Nørgaard udfører vi alle typer belægningsarbejde for private og erhverv med fokus på korrekt opbygget bærelag, præcis afretning og holdbar afvanding.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto">
            <a
              href="tel:+4520483818"
              className="inline-flex items-center justify-center gap-2 rounded bg-brand-orange-500 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-orange-600 transition-colors shadow-md text-center"
            >
              <i className="fa-solid fa-phone" /> Ring 20 48 38 18
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded border border-white/20 bg-white/10 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20 transition-colors text-center"
            >
              <i className="fa-solid fa-calculator" /> Indhent tilbud
            </Link>
          </div>
        </div>
      </div>

      {/* Sektion 1: Hemmeligheden bag en holdbar belægning */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500/10 text-brand-orange-500 flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-layer-group" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Grundigt underarbejde
            </span>
            <h3 className="font-display text-2xl font-bold text-brand-navy-900">
              Hemmeligheden ligger under overfladen
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-700 leading-relaxed space-y-4 text-base md:text-lg">
          <p>
            Hemmeligheden bag en belægning, der forbliver jævn og flot i årtier, ligger i arbejdet under fliserne. Hvis underlaget ikke udgraves til den korrekte bæredygtige bund eller komprimeres grundigt, vil der uundgåeligt opstå kørespor, sætningsskader og vandpytter med tiden. Hos Entreprenør Jan Nørgaard sikrer vi, at hele underbygningen udføres efter forskrifterne.
          </p>
          <p>
            Vi lægger stor vægt på korrekt udgravning, udlægning af bundsikringsgrus og komprimering af stabilgrus. Samtidig tænker vi afvanding og terrænfald ind fra starten: oplever du våde områder eller fugt ved fundamentet, kan det kombineres med effektiv{' '}
            <Link
              to="/ydelser/draen-etablering-reparation"
              className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
            >
              etablering af dræn
            </Link>
            , ligesom det ofte er fordelagtigt at få foretaget grundig{' '}
            <Link
              to="/ydelser/kloakspuling"
              className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
            >
              kloakspuling
            </Link>{' '}
            og rensning af brønde, inden det nye faste underlag og fliserne lægges endeligt.
          </p>
        </div>

        {/* 4 Kerneelementer */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pavingFeatures.map((item, idx) => (
            <div
              key={idx}
              className="rounded-xl border border-brand-navy-100 bg-brand-navy-50/40 p-5 hover:bg-white hover:border-brand-orange-300 hover:shadow-xs transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-brand-navy-100 text-brand-navy-700">
                  {item.badge}
                </span>
                <div className="h-9 w-9 rounded-lg bg-brand-navy-900 text-brand-orange-400 flex items-center justify-center text-base">
                  <i className={item.icon} />
                </div>
              </div>
              <h4 className="font-display text-lg font-bold text-brand-navy-900 mb-1.5">
                {item.title}
              </h4>
              <p className="text-sm text-brand-navy-600 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Sektion 2: Sammenhæng med dræn og kloakforhold */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500/10 text-brand-orange-500 flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-arrows-split-up-and-left" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Afvanding & Helhedssikring
            </span>
            <h3 className="font-display text-2xl font-bold text-brand-navy-900">
              Afvanding, dræn og kontrol af underjordiske rør
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-700 leading-relaxed space-y-4 text-base md:text-lg">
          <p>
            En flot terrasse eller indkørsel må aldrig blive en kilde til fugtproblemer for huset. Ligger belægningen for tæt på soklen eller falder den ind mod facaden, risikerer du fugt i murværk og kælder. Derfor planlægger vi altid korrekt afvanding, linjedræn og terrænfald som en integreret del af belægningen.
          </p>
          <p>
            Står grunden under vand i perioder, eller har du en fugtig sokkel, kan det være nødvendigt at kombinere belægningsarbejdet med et nyt{' '}
            <Link
              to="/ydelser/draen-etablering-reparation"
              className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
            >
              dræn eller omfangsdræn
            </Link>
            . Det er langt mere økonomisk at etablere drænrør, mens maskinerne alligevel er på pladsen til udgravning, end at skulle bryde en nylagt belægning op efterfølgende.
          </p>
          <p>
            Ligeledes anbefaler vi at undersøge og eventuelt spule tagnedløbsrør og kloakledninger, der løber under det nye fliseareal. Med vores udstyr til{' '}
            <Link
              to="/ydelser/kloakspuling"
              className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
            >
              kloakspuling
            </Link>{' '}
            og TV-inspektion sikrer vi, at alle underjordiske rør er 100% fri for rødder og fedtaflejringer, før fliserne lægges.
          </p>
        </div>
      </section>

      {/* Sektion 3: Arbejdsprocessen ved belægning */}
      <section className="bg-brand-navy-900 text-white rounded-xl p-6 md:p-8 shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 h-40 w-40 rounded-full bg-brand-orange-500/10 pointer-events-none" />
        <div className="max-w-3xl mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-brand-orange-400 text-xs font-bold uppercase tracking-wider mb-2">
            <i className="fa-solid fa-list-check" /> Arbejdsproces
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-white">
            Sådan udfører vi dit belægningsarbejde
          </h3>
          <p className="mt-2 text-brand-navy-200 text-base md:text-lg leading-relaxed">
            Vi følger en gennemprøvet proces, der sikrer en fast og stabil flisebelægning uden ubehagelige overraskelser.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {processSteps.map((stepItem, idx) => (
            <div
              key={idx}
              className="bg-brand-navy-800/80 border border-brand-navy-700 rounded-xl p-6 flex flex-col justify-between relative group hover:border-brand-orange-500/50 transition-colors"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-display text-2xl font-black text-brand-orange-400">
                    {stepItem.step}
                  </span>
                  <div className="h-10 w-10 rounded-lg bg-brand-navy-950 text-brand-orange-400 flex items-center justify-center text-lg shadow-xs">
                    <i className={stepItem.icon} />
                  </div>
                </div>
                <h4 className="font-display text-lg font-bold text-white mb-2">
                  {stepItem.title}
                </h4>
                <p className="text-sm text-brand-navy-200 leading-relaxed">
                  {stepItem.text}
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-brand-navy-700/60 text-[11px] font-bold uppercase tracking-wider text-brand-orange-300">
                {stepItem.phase}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sektion 4: Hvad afhænger prisen på belægning af? */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="max-w-3xl mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-brand-orange-500/10 text-brand-orange-600 flex items-center justify-center text-base">
              <i className="fa-duotone fa-calculator" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Gennemskuelighed
            </span>
          </div>
          <h3 className="font-display text-2xl font-bold text-brand-navy-900">
            Hvad afhænger prisen på belægning af?
          </h3>
          <p className="mt-2 text-brand-navy-700 text-base md:text-lg leading-relaxed">
            Prisen på professionelt flisearbejde varierer alt efter projektets omfang, stenvalg og jordbundsforhold. Vi udarbejder altid et gennemskueligt og uforpligtende tilbud på din opgave.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {priceFactors.map((factor, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-brand-navy-100 bg-brand-navy-50/40 p-4 flex flex-col justify-between"
            >
              <div>
                <div className="h-9 w-9 rounded bg-brand-navy-900 text-brand-orange-400 flex items-center justify-center text-sm mb-3">
                  <i className={factor.icon} />
                </div>
                <h4 className="font-display text-base font-bold text-brand-navy-900 mb-1">
                  {factor.title}
                </h4>
                <p className="text-xs md:text-sm text-brand-navy-600 leading-relaxed">
                  {factor.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Interne SEO links til beslægtede ydelser */}
      <section className="bg-white rounded-xl border border-brand-navy-200/90 p-6 md:p-8 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 pb-4 border-b border-brand-navy-100">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Relaterede SEO ydelser
            </span>
            <h3 className="font-display text-xl md:text-2xl font-bold text-brand-navy-900 mt-0.5">
              Se også vores beslægtede fagområder
            </h3>
          </div>
          <p className="text-xs md:text-sm text-brand-navy-500">
            Samlet ekspertise inden for dræn, kloak og anlægsarbejde
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/ydelser/draen-etablering-reparation"
            className="group p-5 rounded-xl border border-brand-navy-200/80 bg-brand-navy-50/40 hover:bg-white hover:border-brand-orange-400 hover:shadow-md transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-brand-navy-900 text-brand-orange-400 group-hover:bg-brand-orange-500 group-hover:text-white transition-colors flex items-center justify-center text-xl shadow-xs">
                  <i className="fa-duotone fa-droplet-slash" />
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-600 group-hover:translate-x-1 transition-transform">
                  Læs om dræn <i className="fa-solid fa-arrow-right text-[11px]" />
                </span>
              </div>
              <h4 className="font-display text-lg font-bold text-brand-navy-900 group-hover:text-brand-orange-600 transition-colors">
                Dræn – Etablering & Reparation
              </h4>
              <p className="mt-2 text-sm text-brand-navy-600 leading-relaxed">
                Undgå vand ved soklen og fugt i kælderen. Vi etablerer omfangsdræn og drænsystemer, der sikrer effektiv vandafledning omkring hele huset.
              </p>
            </div>
          </Link>

          <Link
            to="/ydelser/kloakspuling"
            className="group p-5 rounded-xl border border-brand-navy-200/80 bg-brand-navy-50/40 hover:bg-white hover:border-brand-orange-400 hover:shadow-md transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-brand-navy-900 text-brand-orange-400 group-hover:bg-brand-orange-500 group-hover:text-white transition-colors flex items-center justify-center text-xl shadow-xs">
                  <i className="fa-duotone fa-truck-droplet" />
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-600 group-hover:translate-x-1 transition-transform">
                  Læs om kloakspuling <i className="fa-solid fa-arrow-right text-[11px]" />
                </span>
              </div>
              <h4 className="font-display text-lg font-bold text-brand-navy-900 group-hover:text-brand-orange-600 transition-colors">
                Professionel Kloakspuling
              </h4>
              <p className="mt-2 text-sm text-brand-navy-600 leading-relaxed">
                Få renset regnvandsbrønde, tagnedløb og afløb grundigt inden eller efter belægningsarbejdet med professionel højtryksspuling.
              </p>
            </div>
          </Link>
        </div>
      </section>

      {/* Sektion 5: Lokal belægning i Sommersted og omegn */}
      <section className="rounded-xl border border-brand-navy-200 bg-gradient-to-br from-white to-brand-navy-50/50 p-6 md:p-8 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
          <div className="space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-navy-900 text-white text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-location-dot text-brand-orange-400" />
              Lokal dækning i Sønderjylland
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-brand-navy-900">
              Lokal belægning i Sommersted og omegn
            </h3>
            <p className="text-brand-navy-700 text-base md:text-lg leading-relaxed">
              Vi har base i Sommersted og udfører belægningsarbejde for både private haveejere, boligforeninger og virksomheder i Jels, Haderslev, Vojens, Vejen, Kolding, Gram og opland.
            </p>
            <p className="text-brand-navy-700 text-base md:text-lg leading-relaxed">
              Som autoriseret kloakmester og erfaren entreprenør tænker vi hele projektet igennem fra råjord til færdig belægning. Kontakt os for en uforpligtende besigtigelse og et fast tilbud på dit projekt.
            </p>
          </div>

          <div className="w-full lg:w-72 shrink-0 bg-white p-5 rounded-lg border border-brand-navy-200 shadow-xs">
            <h4 className="font-display text-xs uppercase tracking-wider font-bold text-brand-navy-500 mb-3">
              Vi dækker følgende byer:
            </h4>
            <div className="flex flex-wrap gap-2">
              {serviceTowns.map((town, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider ${
                    town.isBase
                      ? 'bg-brand-orange-500 text-white shadow-xs'
                      : 'bg-brand-navy-100 text-brand-navy-800'
                  }`}
                >
                  <i className={`fa-solid ${town.isBase ? 'fa-house-flag' : 'fa-location-dot text-brand-orange-600'}`} />
                  {town.name}
                  {town.isBase && <span className="text-[10px] opacity-80">(Base)</span>}
                </span>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-brand-navy-100">
              <Link
                to="/kontakt"
                className="w-full inline-flex items-center justify-center gap-2 rounded bg-brand-navy-900 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-navy-800 transition-colors text-center"
              >
                Kontakt Jan Nørgaard
                <i className="fa-solid fa-arrow-right text-brand-orange-400" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
